import type { Action } from 'redux';
import {
  buffers, type TakeableChannel,
} from 'redux-saga';
import {
  actionChannel, call, put, take,
} from 'redux-saga/effects';
import type { LatLngBounds } from '~/_shared/types/latLng';
import type { ArrayElement } from '~/_shared/utils/array/array.helpers';
import {
  createUuid, type Uuid,
} from '~/_shared/utils/createUuid';
import { select } from '~/_shared/utils/saga/effects';
import type { BoundaryGroup } from '~/boundary/boundary.types';
import { boundaryGroupsSelector } from '~/store/boundaryGroups/boundaryGroups.selectors';
import { isBoundaryGroupRenderable } from '~/store/boundaryItems/boundaryItems.helpers';
import { waitForBoundaryGroups } from '~/store/boundaryItems/boundaryItems.sagas';
import {
  boundaryTerritoryDetailsZoomedLoadingError,
  boundaryTerritoryDetailsZoomedLoadingStart, boundaryTerritoryDetailsZoomedLoadingSuccess,
} from '~/store/boundaryTerritoryDetails/boundaryTerritoryDetails.actionCreators';
import { BOUNDARY_TERRITORY_DETAILS_FETCH_DETAILS_REQUEST } from '~/store/boundaryTerritoryDetails/boundaryTerritoryDetails.actionTypes';
import { getBtgExtraDataQuery } from '~/store/boundaryTerritoryDetails/boundaryTerritoryDetails.helpers';
import {
  type BoundaryTerritoryGroupDetailsRequestQueryModel, getBoundaryTerritoryGroupDetails,
} from '~/store/boundaryTerritoryDetails/boundaryTerritoryDetails.repository';
import type { BoundaryTerritoryGroupDetails } from '~/store/boundaryTerritoryDetails/boundaryTerritoryDetails.types';
import { boundaryTerritoryGroupsSelector } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.selectors';
import type { BoundaryTerritoryGroup } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.state';
import { MAP_COMPONENT_SET_LAST_BOUNDS } from '~/store/frontendState/mapComponent/mapComponent.actionTypes';
import { mapComponentLastBoundsSelector } from '~/store/frontendState/mapComponent/mapComponent.selectors';
import { primarySpreadsheetIdSelector } from '~/store/mapInfo/mapInfo.selectors';
import { waitForMapSettingsReady } from '~/store/mapSettings/ready/mapSettingsReady.sagas.helpers';
import {
  type BoundaryFilters,
  boundaryFiltersSelector,
} from '~/store/mapSettings/toolsState/boundaryTerritoryGroups/mapSettingsBTGToolsState.selectors';
import { clientIdSelector } from '~/store/selectors/useClientIdSelector';
import type { FilterTreeItemRequest } from '~/store/spreadsheetData/filtering/spreadsheetDataFiltering.helpers';
import { getStoreFilterTree } from '~/store/spreadsheetData/filterTree/spreadsheetDataFilterTree.helpers';

export function* watchForLoadingOfZoomedInBoundaryDetails() {
  const mapBoundsChannel: TakeableChannel<Action> = yield actionChannel(
    [BOUNDARY_TERRITORY_DETAILS_FETCH_DETAILS_REQUEST, MAP_COMPONENT_SET_LAST_BOUNDS],
    buffers.sliding(1)
  );

  while (true) {
    yield take(mapBoundsChannel);
    yield call(waitForMapSettingsReady);
    yield call(waitForBoundaryGroups);

    const clientId: number | null = yield select<number | null>(clientIdSelector);
    const boundaryGroups: ReadonlyArray<BoundaryGroup> = yield select(boundaryGroupsSelector);
    const BTGs: ReadonlyArray<BoundaryTerritoryGroup> = yield select(boundaryTerritoryGroupsSelector);
    const spreadsheetId: number | null = yield select<number | null>(primarySpreadsheetIdSelector);
    const mapBounds: ReturnType<typeof mapComponentLastBoundsSelector> = yield select(mapComponentLastBoundsSelector);
    const boundaryFilters: BoundaryFilters = yield select<BoundaryFilters>(boundaryFiltersSelector);

    if ((clientId === null) || (spreadsheetId === null) || !mapBounds) {
      continue;
    }

    const filteredBTGs = filterBTGsWithAppropriateZoomLevel(BTGs, boundaryGroups, mapBounds.zoomLevel, boundaryFilters);

    if (filteredBTGs.length === 0) {
      continue;
    }

    yield put(boundaryTerritoryDetailsZoomedLoadingStart());

    const btgWithQueryId = new Map<Uuid, BoundaryTerritoryGroup>(
      filteredBTGs.map((btg: BoundaryTerritoryGroup) => [createUuid(), btg])
    );

    let combinedFilterTrees: FilterTreeItemRequest | null = null;
    try {
      combinedFilterTrees = yield call(getStoreFilterTree);
    }
    catch (e) {
      yield put(boundaryTerritoryDetailsZoomedLoadingError(e));
      continue;
    }

    const requests = prepareBTGDetailsRequests(
      btgWithQueryId,
      spreadsheetId,
      combinedFilterTrees,
      mapBounds.bounds.getBounds(),
    );

    yield call(fetchAndDispatchBTGDetails, clientId, requests, btgWithQueryId, combinedFilterTrees?.filterHash ?? '');
  }
}

function filterBTGsWithAppropriateZoomLevel(
  activeBTGs: ReadonlyArray<BoundaryTerritoryGroup>,
  boundaryGroups: ReadonlyArray<BoundaryGroup>,
  zoomLevel: number,
  boundaryFilters: BoundaryFilters,
): ReadonlyArray<BoundaryTerritoryGroup> {
  return activeBTGs.filter(btg => {
    const bg = boundaryGroups.find(group => group.id === btg.boundaryGroupId);

    if (!bg) {
      return false;
    }

    const supportsCurrentZoom = bg.zoomLevels.some(level => level <= zoomLevel);
    const requiresZoom = !bg.zoomLevels.includes(0);

    return supportsCurrentZoom && requiresZoom && isBoundaryGroupRenderable(zoomLevel, bg, btg, boundaryFilters);
  });
}

function prepareBTGDetailsRequests(
  btgWithQueryId: Map<Uuid, BoundaryTerritoryGroup>,
  spreadsheetId: number,
  combinedFilterTrees: FilterTreeItemRequest | null,
  bounds: LatLngBounds,
): BoundaryTerritoryGroupDetailsRequestQueryModel[] {
  const requests: BoundaryTerritoryGroupDetailsRequestQueryModel[] = [];
  btgWithQueryId.forEach((btg, queryId) => {
    requests.push({
      boundaryTerritoryGroup: { ...btg },
      boundaryTerritoryGroupQueryId: queryId,
      filter: btg.settings.ignoreFilters || !combinedFilterTrees?.filterTree ? null : {
        filter_tree: combinedFilterTrees.filterTree,
      },
      boundingBox: bounds,
      extraData: getBtgExtraDataQuery(btg, spreadsheetId),
    });
  });
  return requests;
}

function* fetchAndDispatchBTGDetails(
  clientId: number,
  requests: BoundaryTerritoryGroupDetailsRequestQueryModel[],
  btgWithQueryId: Map<Uuid, BoundaryTerritoryGroup>,
  filterHash: string,
) {
  try {
    const response: Map<Uuid, BoundaryTerritoryGroupDetails> = yield call(
      getBoundaryTerritoryGroupDetails, clientId, requests
    );

    const actionResults: Array<ArrayElement<Parameters<typeof boundaryTerritoryDetailsZoomedLoadingSuccess>[0]>> = [];

    for (const [queryId, details] of response.entries()) {
      const btg = btgWithQueryId.get(queryId);

      if (btg) {
        actionResults.push({
          boundaryTerritoryGroupId: btg.boundaryTerritoryGroupId,
          details,
        });
      }
    }

    yield put(boundaryTerritoryDetailsZoomedLoadingSuccess(actionResults, filterHash));
  }
  catch (e) {
    yield put(boundaryTerritoryDetailsZoomedLoadingError(e));
  }
}
